import { Box, Stack, Typography, Button, Chip, Tooltip, useTheme } from "@mui/material";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import React from "react";
import { useTranslation } from "react-i18next";
import { Tenant } from "../../types";
import { ConfirmationDialog } from "../../widgets/ConfirmationDialog/ConfirmationDialog";

interface HoldingAreaListItemProps {
  tenant: Tenant;
  onAssign: (tenantId: string) => void;
  onEdit: (tenant: Tenant) => void;
  onRemove: (tenantId: string) => void;
  disabled?: boolean;
}

/**
 * Individual list item for aircraft in holding area
 * Shows thumbnail, tail number, model, and assign button
 */
export const HoldingAreaListItem: React.FC<HoldingAreaListItemProps> = ({
  tenant,
  onAssign,
  onEdit,
  onRemove,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [imageError, setImageError] = React.useState(false);
  const [noteViewed, setNoteViewed] = React.useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);

  const handleRemoveClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmRemove = () => {
    setConfirmDialogOpen(false);
    onRemove(tenant.id);
  };

  const handleCancelRemove = () => {
    setConfirmDialogOpen(false);
  };

  const identifier = tenant.tail_number || tenant.aircraft?.model || "this aircraft";

  return (
    <Box sx={{ position: "relative" }}>
      {/* Note Badge - appears at top-left corner */}
      {tenant.note && !noteViewed && (
        <Box
          sx={{
            position: "absolute",
            top: -6,
            left: -6,
            width: 12,
            height: 12,
            borderRadius: "50%",
            backgroundColor: "warning.main",
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            zIndex: 10,
          }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
      {/* Aircraft Thumbnail */}
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 1,
          backgroundColor: "action.hover",
          flexShrink: 0,
          mr: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {tenant.aircraft?.image && !imageError ? (
          <img
            src={tenant.aircraft.image}
            alt={tenant.tail_number}
            onError={() => setImageError(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: 8,
            }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            {t("screens.multiHangar.holdingAreaListItem.imageAlt")}
          </Typography>
        )}
      </Box>

      {/* Aircraft Info */}
      <Stack flex={1} spacing={0.5}>
        {/* Header: Tail Number + Type Chip */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{ fontSize: "0.95rem", lineHeight: 1.2 }}
          >
            {tenant.tail_number}
          </Typography>
          <Chip
            label={
              tenant.type === "transient"
                ? t("screens.multiHangar.holdingAreaListItem.tenantTypes.transient")
                : t("screens.multiHangar.holdingAreaListItem.tenantTypes.base")
            }
            size="small"
            color={tenant.type === "transient" ? "primary" : "secondary"}
            variant="outlined"
            sx={{ fontSize: "0.65rem", fontWeight: 500, height: 20 }}
          />
          {tenant.note && (
            <Tooltip
              title={tenant.note}
              arrow
              placement="top"
              onOpen={() => setNoteViewed(true)}
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: 'background.paper',
                    color: 'text.primary',
                    fontSize: '12px',
                    borderRadius: '5px',
                    padding: '8px 12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    maxWidth: 250,
                    border: 1,
                    borderColor: 'divider',
                    '&::before': {
                      backgroundColor: 'warning.main',
                      content: '""',
                      display: 'block',
                      height: '4px',
                      margin: '-8px -12px 6px -12px',
                      borderTopLeftRadius: '5px',
                      borderTopRightRadius: '5px',
                    }
                  }
                },
                arrow: {
                  sx: {
                    color: 'background.paper',
                  }
                }
              }}
            >
              <StickyNote2Icon
                sx={{
                  fontSize: 16,
                  color: 'warning.main',
                  cursor: 'help'
                }}
              />
            </Tooltip>
          )}
        </Stack>

        {/* Aircraft Model - Full Width */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.8rem", lineHeight: 1.2 }}
        >
          {tenant.aircraft?.model || t("screens.multiHangar.holdingAreaListItem.unknownModel")}
        </Typography>
      </Stack>

      {/* Action Buttons */}
      <Stack spacing={0.5} sx={{ minWidth: 'fit-content' }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => onAssign(tenant.id)}
          disabled={disabled}
          sx={{ py: 0.25, fontSize: "0.7rem", minHeight: 24 }}
        >
          {t("screens.multiHangar.holdingAreaListItem.buttons.assign")}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onEdit(tenant)}
          disabled={disabled}
          sx={{ py: 0.25, fontSize: "0.7rem", minHeight: 24 }}
        >
          {t("screens.multiHangar.holdingAreaListItem.buttons.edit")}
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="error"
          onClick={handleRemoveClick}
          disabled={disabled}
          sx={{ py: 0.25, fontSize: "0.7rem", minHeight: 24 }}
        >
          {t("screens.multiHangar.holdingAreaListItem.buttons.remove")}
        </Button>
      </Stack>
      </Box>

      {/* Confirmation Dialog */}
      {confirmDialogOpen && (
        <ConfirmationDialog
          title={t("screens.multiHangar.holdingAreaListItem.confirmDialog.title")}
          text={t("screens.multiHangar.holdingAreaListItem.confirmDialog.text", { identifier })}
          okText={t("screens.multiHangar.holdingAreaListItem.confirmDialog.okButton")}
          cancelText={t("screens.multiHangar.holdingAreaListItem.confirmDialog.cancelButton")}
          okButtonColor="error"
          onOk={handleConfirmRemove}
          onCancel={handleCancelRemove}
          onClose={handleCancelRemove}
        />
      )}
    </Box>
  );
};
